import React from "react";
import {
  Phone, MessageCircle, ArrowRight, Microscope, Camera, Eye, Video,
  HeartHandshake, Accessibility, Bandage, Briefcase, MapPin, AlertCircle, IndianRupee, Printer,
} from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import FAQAccordion from "@/components/FAQAccordion";
import AppointmentForm from "@/components/AppointmentForm";
import { JsonLd } from "@/components/StructuredData";
import { CLINIC } from "@/lib/clinicInfo";

export const HOME_VISIT_SERVICE = "Home Eye Check-up (Noida & Greater Noida)";
export const HOME_VISIT_WA = `https://wa.me/${CLINIC.phoneRaw.replace("+", "")}?text=${encodeURIComponent(
  "Hello, I would like to book a Home visit eye check-up. Patient name: , Age: , Area (Noida / Greater Noida): "
)}`;

const TESTS = [
  { icon: Microscope, name: "Portable Slit Lamp Examination", desc: "Detailed examination of the eyelids, cornea, lens and front of the eye — screens for cataract, infections, dry eye and lid problems." },
  { icon: Camera, name: "Undilated Fundus Photography", desc: "Portable retinal photographs taken without dilating drops — screens the retina and optic nerve for diabetic, hypertensive and glaucoma-related changes." },
  { icon: Eye, name: "Vision & Refraction", desc: "Visual acuity testing and refraction to check your glasses power and whether a new prescription is needed." },
  { icon: Video, name: "Virtual Consult with Dr. Gurkiran Kaur", desc: "Your findings are reviewed by Dr. Kaur over a video consultation from the comfort of your home, with a clear plan for next steps." },
];

const FOR_WHOM = [
  { icon: HeartHandshake, t: "Elderly & bedridden patients", d: "No travel, no waiting rooms — the eye check-up comes to their bedside or living room." },
  { icon: Bandage, t: "Recovering after surgery", d: "For patients advised rest after any surgery or hospital stay who still need their eyes checked." },
  { icon: Accessibility, t: "People with disabilities", d: "For those with limited mobility for whom a clinic visit is difficult or tiring." },
  { icon: Briefcase, t: "Busy professionals", d: "A thorough eye check-up at home or before work, without losing half a day to commuting." },
];

const STEPS = [
  { n: "01", t: "Book", d: "Fill the form, call or WhatsApp us with the patient's details and address." },
  { n: "02", t: "We visit", d: "Our team arrives with portable slit lamp, fundus camera and vision testing equipment." },
  { n: "03", t: "Doctor consult", d: "Dr. Gurkiran Kaur reviews the findings with you on a virtual consultation." },
  { n: "04", t: "Your plan", d: "Prescription, advice or — if needed — a clinic referral for advanced tests or surgery." },
];

const FAQS = [
  { q: "Which areas do you cover for home eye check-ups?", a: "We currently provide home eye check-ups across Noida and Greater Noida." },
  { q: "What tests are done during the home visit?", a: "Portable slit lamp examination, undilated fundus photography, vision and refraction testing, followed by a virtual consultation with Dr. Gurkiran Kaur." },
  { q: "Is the home eye check-up suitable for children?", a: "The home eye check-up is not recommended for children under 15 years. Children need a full clinic-based examination, including dilated and cycloplegic tests, which we offer at our Sector 32 Noida clinic." },
  { q: "How much does a home eye check-up cost?", a: "Please call or WhatsApp us for current home visit charges." },
  { q: "Will I still need to visit the clinic?", a: "Most routine concerns can be assessed at home. If a condition needs advanced diagnostics (OCT, perimetry, dilated retina exam) or surgery, Dr. Kaur will advise a clinic visit." },
];

const schema = {
  "@context": "https://schema.org",
  "@type": "MedicalProcedure",
  name: "Home Eye Check-up in Noida",
  procedureType: "https://schema.org/NoninvasiveProcedure",
  description: "At-home eye check-up in Noida and Greater Noida — portable slit lamp, undilated fundus photography, vision and refraction, and a virtual consult with Dr. Gurkiran Kaur.",
  howPerformed: "Portable slit lamp examination, undilated fundus photography and refraction at the patient's home, followed by a virtual consultation.",
  provider: { "@type": "MedicalBusiness", name: CLINIC.name, telephone: CLINIC.phoneRaw },
  areaServed: [{ "@type": "City", name: "Noida" }, { "@type": "City", name: "Greater Noida" }],
};

const btn = "inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm";

function Ctas({ prefix }) {
  return (
    <div className="flex flex-wrap gap-3">
      <a href="#book" className={`${btn} bg-primary text-primary-foreground hover:bg-primary/90`} data-testid={`${prefix}-book`}>
        Book a Home Visit <ArrowRight className="h-4 w-4" />
      </a>
      <a href={HOME_VISIT_WA} target="_blank" rel="noopener noreferrer" data-track-location="Home visit WhatsApp" className={`${btn} border border-border hover:bg-secondary`} data-testid={`${prefix}-whatsapp`}>
        <MessageCircle className="h-4 w-4" /> WhatsApp for Home Visit
      </a>
      <a href={`tel:${CLINIC.phoneRaw}`} className={`${btn} text-primary`} data-testid={`${prefix}-call`}>
        <Phone className="h-4 w-4" /> Call {CLINIC.phone}
      </a>
    </div>
  );
}

function Hero() {
  return (
    <section className="container-wide pt-8 pb-16 overflow-x-clip lg:overflow-visible">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="text-xs uppercase tracking-widest text-accent mb-3">Home Eye Check-up · Noida & Greater Noida</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05]">
            Specialist eye care, <span className="text-primary italic">in the comfort of your home.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-foreground/75 max-w-xl leading-7">
            Portable slit lamp, undilated fundus photography, vision &amp; refraction — and a virtual consult with <strong className="text-foreground">Dr. Gurkiran Kaur</strong>. Designed for those who cannot easily come to the clinic.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/5 border border-primary/20 px-3 py-1.5 text-primary" data-testid="hv-area-badge"><MapPin className="h-3.5 w-3.5" /> Noida &amp; Greater Noida</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 border border-accent/30 px-3 py-1.5 text-foreground/80" data-testid="hv-age-badge"><AlertCircle className="h-3.5 w-3.5" /> Not recommended for children under 15 yrs</span>
          </div>
          <div className="mt-8"><Ctas prefix="hv-hero" /></div>
        </div>
        <div className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -inset-2 sm:-inset-4 bg-accent/10 rounded-3xl -z-10 -rotate-2" />
            <img src="/images/home-eye-checkup.jpg" alt="Home eye check-up for an elderly patient in Noida by Nehchal Eye Centre" className="rounded-2xl border border-border w-full h-[380px] object-cover" loading="eager" width="600" height="380" />
          </div>
        </div>
      </div>
    </section>
  );
}

function CardGrid({ items, testPrefix, cols }) {
  return (
    <div className={`grid gap-4 ${cols}`}>
      {items.map(({ icon: Icon, name, t, desc, d }) => (
        <div key={name || t} className="rounded-xl border border-border bg-card p-6 hover:border-primary/40 transition-colors" data-testid={`${testPrefix}-${(name || t).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4"><Icon className="h-5 w-5" /></div>
          <div className="font-serif text-lg text-foreground">{name || t}</div>
          <p className="mt-2 text-sm text-foreground/70 leading-6">{desc || d}</p>
        </div>
      ))}
    </div>
  );
}

function Steps() {
  return (
    <section className="container-wide py-16" data-testid="hv-steps">
      <div className="text-xs uppercase tracking-widest text-accent mb-3">How it works</div>
      <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-8">Four simple steps.</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {STEPS.map((s) => (
          <div key={s.n} className="rounded-xl border border-border bg-card p-6">
            <div className="font-serif text-3xl text-primary/40">{s.n}</div>
            <div className="font-serif text-lg mt-2">{s.t}</div>
            <p className="mt-2 text-sm text-foreground/70 leading-6">{s.d}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 rounded-xl border border-border bg-secondary/40 p-5 flex flex-wrap items-center gap-3 text-sm" data-testid="hv-charges-note">
        <IndianRupee className="h-4 w-4 text-primary" />
        <span className="text-foreground/80">Charges: <strong className="text-foreground">Call or WhatsApp for charges.</strong></span>
        <Link to="/eye-checkup-at-home/flyer" className="ml-auto inline-flex items-center gap-1.5 text-primary hover:opacity-80" data-testid="hv-flyer-link"><Printer className="h-4 w-4" /> Printable flyer</Link>
      </div>
    </section>
  );
}

export default function HomeVisit() {
  return (
    <div data-testid="home-visit-page">
      <Seo
        title="Home Eye Check-up in Noida & Greater Noida | Dr. Gurkiran Kaur"
        description="At-home eye check-up in Noida & Greater Noida — portable slit lamp, undilated fundus photo, vision & refraction and a virtual consult with Dr. Gurkiran Kaur. Ideal for elderly & bedridden patients."
        path="/eye-checkup-at-home"
      />
      <JsonLd data={schema} />
      <Breadcrumbs items={[{ name: "Home Eye Check-up", path: "/eye-checkup-at-home" }]} />
      <Hero />

      <section className="border-y border-border bg-secondary/40 py-16" data-testid="hv-tests">
        <div className="container-wide">
          <div className="text-xs uppercase tracking-widest text-accent mb-3">What's included</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-8">A complete eye check-up, brought to you.</h2>
          <CardGrid items={TESTS} testPrefix="hv-test" cols="md:grid-cols-2" />
        </div>
      </section>

      <section className="container-wide py-16" data-testid="hv-for-whom">
        <div className="text-xs uppercase tracking-widest text-accent mb-3">Who it's for</div>
        <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-8">For anyone who can't easily come to the clinic.</h2>
        <CardGrid items={FOR_WHOM} testPrefix="hv-for" cols="sm:grid-cols-2 lg:grid-cols-4" />
        <p className="mt-6 text-sm text-foreground/70 flex items-start gap-2" data-testid="hv-children-note">
          <AlertCircle className="h-4 w-4 text-accent mt-0.5 shrink-0" />
          Not recommended for children under 15 years — children need a full clinic-based examination at our Sector 32 Noida clinic.
        </p>
      </section>

      <div className="border-t border-border"><Steps /></div>

      <FAQAccordion faqs={FAQS} title="Home eye check-up — your questions answered" />

      <section id="book" className="bg-secondary/40 border-t border-border py-20" data-testid="hv-book">
        <div className="container-wide grid grid-cols-1 lg:grid-cols-2 gap-10 [&>*]:min-w-0">
          <div>
            <div className="text-xs uppercase tracking-widest text-accent mb-3">Book a Home Visit</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-4">We'll come to you.</h2>
            <p className="text-foreground/70 leading-7 mb-6">
              Share the patient's details and your address in Noida or Greater Noida in the message box. Our team will call to confirm a convenient slot.
            </p>
            <Ctas prefix="hv-book" />
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
            <AppointmentForm defaultService={HOME_VISIT_SERVICE} />
          </div>
        </div>
      </section>
    </div>
  );
}
